import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'

// Default payload mirrors the shape produced by buildSprayLogData in
// server/src/hmi/Herbicide/sprayCompletionPipeline.js — every field that flows
// into generateSprayLogPDF.
const initialPayload = () => ({
  cleanDate: new Date().toLocaleDateString('en-US'),
  physicalLocation: 'Tatum Ranch HOA',
  physicalAddress: '5400 E Tatum Blvd, Phoenix AZ 85032',
  // Applicator block — flip variant to switch between HMI and sub presentation
  bizName: 'Desert Greens Spray LLC',
  pmdNum: 'PMD-9001',
  qualifyingParty: 'Jane Applicator',
  qpNum: 'AZ-12345',
  techName: 'Jane Applicator',
  licenseNum: 'AZ-12345',
  licenseExp: '12/31/2027',
  headerName: 'Desert Greens Spray LLC',
  // Weather + timing
  startTime: '7:15am',
  stopTime: '11:45am',
  windDir: 'NW',
  windSpeed: 4.2,
  temp: 82.5,
  mixRate: '2.5%',
  // Water
  waterStart: 500,
  waterEnd: 320,
  waterUsed: 180,
  waterVolume: 180,
  // Misc
  comments: 'Treated emergent weeds along curb lines and decomposed-granite paths. Avoided ornamental beds.',
  source: 'subcontractor',
  telephone: '(602) 555-0190',
  incomingSolutions: [
    {
      name: 'Makaze',
      productName: 'Makaze',
      amount: '6',
      unit: 'oz',
      amountUsed: '6',
      epaRegistration: '524-549',
      url: 'https://www.cdms.net/ldat/ld6S3000.pdf',
      targetPest: 'Ornamental/Right of Way',
      concentration: null
    },
    {
      name: 'Surge',
      productName: 'Surge',
      amount: '3',
      unit: 'oz',
      amountUsed: '3',
      epaRegistration: '8033-105-10163',
      url: '',
      targetPest: 'Ornamental/Right of Way',
      concentration: null
    },
    {
      name: 'Blue Dye',
      productName: 'Blue Dye',
      amount: '2',
      unit: 'oz',
      amountUsed: '2',
      epaRegistration: '',
      url: '',
      targetPest: 'Ornamental/Right of Way',
      concentration: null
    }
  ],
  chemicalDeclaration: null,
  postSprayReport: null,
  // New: subcontractor-format branding (only used when format === 'subcontractor')
  vendorBranding: {
    displayName: 'Desert Greens Spray',
    primaryColor: '#3f7a5a',
    logoUrl: 'https://placehold.co/240x80/3f7a5a/ffffff/png?text=Desert+Greens'
  }
})

// Presets for quickly flipping between the two licensing presentations
const SUB_PRESET = {
  bizName: 'Desert Greens Spray LLC',
  pmdNum: 'PMD-9001',
  qualifyingParty: 'Jane Applicator',
  qpNum: 'AZ-12345',
  techName: 'Jane Applicator',
  licenseNum: 'AZ-12345',
  licenseExp: '12/31/2027',
  headerName: 'Desert Greens Spray LLC',
  source: 'subcontractor'
}
const HMI_PRESET = {
  bizName: 'HMI Commercial Landscaping',
  pmdNum: 'QA 31413',
  qualifyingParty: 'John English',
  qpNum: '9408',
  techName: 'Subcontractor',
  licenseNum: '',
  licenseExp: '',
  headerName: 'HMI Commercial Landscaping\nPowered By Environmental Pest Solutions, LLC',
  source: 'subcontractor'
}

const labelStyle = { fontSize: 11, color: '#607d8b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #cfd9dc', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }
const sectionStyle = { background: '#fff', borderRadius: 6, padding: 14, marginBottom: 12, border: '1px solid #e0e8eb' }

function SprayPdfPreviewPage({ url }) {
  const [payload, setPayload] = useState(initialPayload())
  const [documentOwner, setDocumentOwner] = useState('subcontractor') // 'hmi' | 'subcontractor' | 'legacy'
  const [pdfDataUrl, setPdfDataUrl] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)

  const setBrand = (key, value) => setPayload(p => ({ ...p, vendorBranding: { ...(p.vendorBranding || {}), [key]: value } }))

  const setField = (key, value) => {
    setPayload(p => ({ ...p, [key]: value }))
  }

  const setChemField = (idx, key, value) => {
    setPayload(p => ({
      ...p,
      incomingSolutions: p.incomingSolutions.map((c, i) => i === idx ? { ...c, [key]: value } : c)
    }))
  }

  const addChem = () => {
    setPayload(p => ({
      ...p,
      incomingSolutions: [...p.incomingSolutions, { name: '', amount: '', unit: 'oz', amountUsed: '', epaRegistration: '', targetPest: 'Ornamental/Right of Way' }]
    }))
  }

  const removeChem = (idx) => {
    setPayload(p => ({
      ...p,
      incomingSolutions: p.incomingSolutions.filter((_, i) => i !== idx)
    }))
  }

  const applyPreset = (preset) => {
    setPayload(p => ({ ...p, ...preset }))
  }

  const generate = useCallback(async () => {
    setGenerating(true)
    setError(null)
    try {
      const base = url || ''
      const body = documentOwner === 'legacy'
        ? { ...payload, format: 'legacy' }
        : { ...payload, documentOwner }
      const res = await axios.post(`${base}/api/spray-pdf-preview`, body)
      setPdfDataUrl(res.data?.dataUrl || null)
    } catch (err) {
      console.error('[SprayPdfPreview] generate error', err)
      setError(err.response?.data?.message || err.message || 'Failed to generate PDF')
    } finally {
      setGenerating(false)
    }
  }, [url, payload, documentOwner])

  const download = () => {
    if (!pdfDataUrl) return
    const a = document.createElement('a')
    a.href = pdfDataUrl
    a.download = `spray-log-preview-${Date.now()}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', background: '#f5f7f8', overflow: 'hidden' }}>
      {/* LEFT — form */}
      <div style={{ flex: '0 0 480px', overflowY: 'auto', padding: 16, borderRight: '1px solid #e0e8eb' }}>
        <h2 style={{ marginTop: 0, marginBottom: 4, fontSize: 18 }}>Spray Log PDF Preview</h2>
        <div style={{ fontSize: 12, color: '#607d8b', marginBottom: 16 }}>
          Edit any field, then click Generate. Hits the real <code>generateSprayLogPDF</code> on the server.
        </div>

        {/* Document owner toggle — who's the doc FOR */}
        <div style={{ fontSize: 11, color: '#607d8b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Document owner</div>
        <div style={{ display: 'flex', gap: 0, marginBottom: 12, border: '1px solid #cfd9dc', borderRadius: 4, overflow: 'hidden' }}>
          <button
            onClick={() => setDocumentOwner('hmi')}
            style={{
              flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
              background: documentOwner === 'hmi' ? '#558b6e' : '#fff',
              color: documentOwner === 'hmi' ? '#fff' : '#607d8b'
            }}
          >
            HMI (our paper)
          </button>
          <button
            onClick={() => setDocumentOwner('subcontractor')}
            style={{
              flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
              borderLeft: '1px solid #cfd9dc',
              background: documentOwner === 'subcontractor' ? '#263238' : '#fff',
              color: documentOwner === 'subcontractor' ? '#fff' : '#607d8b'
            }}
          >
            Subcontractor
          </button>
          <button
            onClick={() => setDocumentOwner('legacy')}
            style={{
              flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
              borderLeft: '1px solid #cfd9dc',
              background: documentOwner === 'legacy' ? '#90a4ae' : '#fff',
              color: documentOwner === 'legacy' ? '#fff' : '#607d8b'
            }}
          >
            Legacy
          </button>
        </div>

        {/* Preset buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button onClick={() => applyPreset(SUB_PRESET)} style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, background: '#e3f2fd', color: '#0d47a1', border: '1px solid #b6d4f5', borderRadius: 4, cursor: 'pointer' }}>
            Sub licensing
          </button>
          <button onClick={() => applyPreset(HMI_PRESET)} style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, background: '#e8f5e9', color: '#2e7d32', border: '1px solid #b6dfc0', borderRadius: 4, cursor: 'pointer' }}>
            HMI licensing
          </button>
          <button onClick={() => setPayload(initialPayload())} style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, background: '#fff3d9', color: '#8c5b00', border: '1px solid #f0c66e', borderRadius: 4, cursor: 'pointer' }}>
            Reset
          </button>
        </div>

        {/* Vendor branding — only relevant when documentOwner === 'subcontractor' */}
        {documentOwner === 'subcontractor' && (
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Vendor branding</div>
            <label style={labelStyle}>Display name</label>
            <input style={inputStyle} value={payload.vendorBranding?.displayName || ''} onChange={e => setBrand('displayName', e.target.value)} />
            <div style={{ height: 8 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', gap: 8 }}>
              <div>
                <label style={labelStyle}>Primary color</label>
                <input style={inputStyle} value={payload.vendorBranding?.primaryColor || ''} onChange={e => setBrand('primaryColor', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>&nbsp;</label>
                <input type="color" value={payload.vendorBranding?.primaryColor || '#3f7a5a'} onChange={e => setBrand('primaryColor', e.target.value)} style={{ width: '100%', height: 30, padding: 0, border: '1px solid #cfd9dc', borderRadius: 4, cursor: 'pointer' }} />
              </div>
            </div>
            <div style={{ height: 8 }} />
            <label style={labelStyle}>Logo URL</label>
            <input style={inputStyle} value={payload.vendorBranding?.logoUrl || ''} onChange={e => setBrand('logoUrl', e.target.value)} placeholder="https://..." />
          </div>
        )}

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Site</div>
          <label style={labelStyle}>Date</label>
          <input style={inputStyle} value={payload.cleanDate} onChange={e => setField('cleanDate', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Physical location</label>
          <input style={inputStyle} value={payload.physicalLocation} onChange={e => setField('physicalLocation', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Physical address</label>
          <input style={inputStyle} value={payload.physicalAddress} onChange={e => setField('physicalAddress', e.target.value)} />
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Applicator / Licensing</div>
          <label style={labelStyle}>Business name (header)</label>
          <input style={inputStyle} value={payload.bizName} onChange={e => setField('bizName', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Header name (multi-line OK)</label>
          <textarea style={{ ...inputStyle, minHeight: 50 }} value={payload.headerName} onChange={e => setField('headerName', e.target.value)} />
          <div style={{ height: 8 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle}>PMD #</label>
              <input style={inputStyle} value={payload.pmdNum} onChange={e => setField('pmdNum', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Qualifying Party</label>
              <input style={inputStyle} value={payload.qualifyingParty} onChange={e => setField('qualifyingParty', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>QP License #</label>
              <input style={inputStyle} value={payload.qpNum} onChange={e => setField('qpNum', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Tech name</label>
              <input style={inputStyle} value={payload.techName} onChange={e => setField('techName', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Tech license #</label>
              <input style={inputStyle} value={payload.licenseNum} onChange={e => setField('licenseNum', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Tech license exp</label>
              <input style={inputStyle} value={payload.licenseExp} onChange={e => setField('licenseExp', e.target.value)} />
            </div>
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Weather / Timing</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle}>Start time</label>
              <input style={inputStyle} value={payload.startTime} onChange={e => setField('startTime', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Stop time</label>
              <input style={inputStyle} value={payload.stopTime} onChange={e => setField('stopTime', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Wind direction</label>
              <input style={inputStyle} value={payload.windDir} onChange={e => setField('windDir', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Wind speed (mph)</label>
              <input style={inputStyle} type="number" value={payload.windSpeed} onChange={e => setField('windSpeed', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Temp (°F)</label>
              <input style={inputStyle} type="number" value={payload.temp} onChange={e => setField('temp', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Mix rate</label>
              <input style={inputStyle} value={payload.mixRate} onChange={e => setField('mixRate', e.target.value)} />
            </div>
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Water Usage</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle}>Start (gal)</label>
              <input style={inputStyle} type="number" value={payload.waterStart} onChange={e => setField('waterStart', Number(e.target.value))} />
            </div>
            <div>
              <label style={labelStyle}>End (gal)</label>
              <input style={inputStyle} type="number" value={payload.waterEnd} onChange={e => setField('waterEnd', Number(e.target.value))} />
            </div>
            <div>
              <label style={labelStyle}>Used (gal)</label>
              <input style={inputStyle} type="number" value={payload.waterUsed} onChange={e => {
                const v = Number(e.target.value)
                setPayload(p => ({ ...p, waterUsed: v, waterVolume: v }))
              }} />
            </div>
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#263238' }}>Chemicals</div>
            <button onClick={addChem} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: '#e8f5e9', color: '#2e7d32', border: '1px solid #b6dfc0', borderRadius: 4, cursor: 'pointer' }}>+ Add chemical</button>
          </div>
          {payload.incomingSolutions.map((chem, idx) => (
            <div key={idx} style={{ padding: 10, background: '#f5f7f8', borderRadius: 4, marginBottom: 8, border: '1px solid #e0e8eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#607d8b' }}>Chemical {idx + 1}</span>
                {payload.incomingSolutions.length > 1 && (
                  <button onClick={() => removeChem(idx)} style={{ padding: '2px 8px', fontSize: 11, color: '#c62828', background: 'transparent', border: '1px solid #e8c0c0', borderRadius: 3, cursor: 'pointer' }}>Remove</button>
                )}
              </div>
              <input style={{ ...inputStyle, marginBottom: 6 }} placeholder="Product name" value={chem.name || ''} onChange={e => {
                setChemField(idx, 'name', e.target.value)
                setChemField(idx, 'productName', e.target.value)
              }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                <input style={inputStyle} placeholder="Amount" value={chem.amount || ''} onChange={e => {
                  setChemField(idx, 'amount', e.target.value)
                  setChemField(idx, 'amountUsed', e.target.value)
                }} />
                <input style={inputStyle} placeholder="Unit (oz / gal / lb)" value={chem.unit || ''} onChange={e => setChemField(idx, 'unit', e.target.value)} />
              </div>
              <input style={{ ...inputStyle, marginBottom: 6 }} placeholder="EPA registration #" value={chem.epaRegistration || ''} onChange={e => setChemField(idx, 'epaRegistration', e.target.value)} />
              <input style={inputStyle} placeholder="Target pest" value={chem.targetPest || ''} onChange={e => setChemField(idx, 'targetPest', e.target.value)} />
            </div>
          ))}
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Notes</div>
          <textarea style={{ ...inputStyle, minHeight: 70 }} value={payload.comments} onChange={e => setField('comments', e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 8, position: 'sticky', bottom: 0, padding: '12px 0', background: 'linear-gradient(to bottom, rgba(245,247,248,0), #f5f7f8 30%)' }}>
          <button
            onClick={generate}
            disabled={generating}
            style={{
              flex: 2, padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#fff',
              background: generating ? '#90a4ae' : '#558b6e',
              border: 'none', borderRadius: 4, cursor: generating ? 'wait' : 'pointer'
            }}
          >
            {generating ? 'Generating…' : 'Generate PDF'}
          </button>
          <button
            onClick={download}
            disabled={!pdfDataUrl}
            style={{
              flex: 1, padding: '10px 16px', fontSize: 13, fontWeight: 700,
              color: pdfDataUrl ? '#263238' : '#90a4ae',
              background: '#fff',
              border: '1px solid ' + (pdfDataUrl ? '#cfd9dc' : '#e0e8eb'),
              borderRadius: 4, cursor: pdfDataUrl ? 'pointer' : 'not-allowed'
            }}
          >
            Download
          </button>
        </div>

        {error && (
          <div style={{ marginTop: 10, padding: 10, background: '#ffe8e8', color: '#c62828', borderRadius: 4, fontSize: 12 }}>
            {error}
          </div>
        )}
      </div>

      {/* RIGHT — PDF preview */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {pdfDataUrl ? (
          <iframe
            title="Spray log PDF preview"
            src={pdfDataUrl}
            style={{ flex: 1, border: 'none', background: '#525659' }}
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#90a4ae', fontSize: 14 }}>
            {generating ? 'Generating PDF…' : 'Click "Generate PDF" to render the spray log.'}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(SprayPdfPreviewPage)
